import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  ArrowRight,
  Hexagon,
  ChevronLeft,
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
} from "lucide-react";
import {
  tagline,
  honeyVarieties,
  qualitySection,
  clients,
  contactInfo,
  footerLinks,
} from "../data/mockData";

/* ─── Hero Section ─── */
const HERO_VIDEO_URL = "https://customer-assets.emergentagent.com/job_mbexim-studio/artifacts/0ld5rkg5_iStock-473134233.mp4";

const HeroSection = () => {
  return (
    <section className="relative h-[85vh] min-h-[600px] overflow-hidden">
      {/* Video Background */}
      <video
        autoPlay
        muted
        loop
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
      >
        <source src={HERO_VIDEO_URL} type="video/mp4" />
      </video>
      <div className="absolute inset-0 bg-black/40" />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-6">
        <h1
          className="text-5xl md:text-7xl lg:text-8xl font-bold text-white uppercase leading-[0.95] max-w-5xl"
          style={{ fontFamily: "'Oswald', sans-serif" }}
        >
          India's Prominent
          <br />
          Supplier of Honey
        </h1>
        <button
          onClick={() => {
            const el = document.querySelector("#contact");
            if (el) el.scrollIntoView({ behavior: "smooth" });
          }}
          className="mt-10 px-8 py-3.5 border-2 border-white text-white text-sm font-bold tracking-[0.2em] uppercase hover:bg-white hover:text-stone-900 transition-all duration-300"
          style={{ fontFamily: "'Oswald', sans-serif" }}
        >
          Make An Enquiry
        </button>
      </div>
    </section>
  );
};

/* ─── Tagline Section ─── */
const TaglineSection = () => (
  <section className="bg-[#FBB550] py-20 px-6">
    <div className="max-w-5xl mx-auto text-center">
      <h2
        className="text-3xl md:text-5xl lg:text-6xl font-bold text-stone-900 uppercase leading-tight"
        style={{ fontFamily: "'Oswald', sans-serif" }}
      >
        {tagline.line1}
        <br />
        {tagline.line2}
        <br />
        {tagline.line3}
      </h2>
      <div className="mt-10">
        <Hexagon className="w-12 h-12 mx-auto text-stone-900/70" strokeWidth={1.5} />
      </div>
    </div>
  </section>
);

/* ─── Flora SVG Icons ─── */
const FloraIcons = {
  flower: () => (
    <svg viewBox="0 0 80 80" fill="none" className="w-full h-full">
      <circle cx="40" cy="32" r="8" fill="currentColor" opacity="0.9" />
      {[0, 60, 120, 180, 240, 300].map((angle, i) => (
        <ellipse
          key={i}
          cx="40"
          cy="18"
          rx="7"
          ry="12"
          fill="currentColor"
          opacity="0.7"
          transform={`rotate(${angle} 40 32)`}
        />
      ))}
      <path d="M40 40 L40 65" stroke="currentColor" strokeWidth="3" strokeLinecap="round" opacity="0.6" />
      <ellipse cx="34" cy="58" rx="8" ry="5" fill="currentColor" opacity="0.4" transform="rotate(-30 34 58)" />
      <ellipse cx="46" cy="55" rx="7" ry="4" fill="currentColor" opacity="0.4" transform="rotate(25 46 55)" />
    </svg>
  ),
  eucalyptus: () => (
    <svg viewBox="0 0 80 80" fill="none" className="w-full h-full">
      <path d="M40 70 L40 20" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" opacity="0.6" />
      {[
        { x: 40, y: 22, rx: 6, ry: 12, rot: -25 },
        { x: 40, y: 22, rx: 6, ry: 12, rot: 25 },
        { x: 40, y: 34, rx: 5.5, ry: 11, rot: -30 },
        { x: 40, y: 34, rx: 5.5, ry: 11, rot: 30 },
        { x: 40, y: 46, rx: 5, ry: 10, rot: -35 },
        { x: 40, y: 46, rx: 5, ry: 10, rot: 35 },
        { x: 40, y: 56, rx: 4.5, ry: 9, rot: -30 },
        { x: 40, y: 56, rx: 4.5, ry: 9, rot: 30 },
      ].map((l, i) => (
        <ellipse key={i} cx={l.x} cy={l.y} rx={l.rx} ry={l.ry} fill="currentColor" opacity="0.65" transform={`rotate(${l.rot} ${l.x} ${l.y})`} />
      ))}
    </svg>
  ),
  litchi: () => (
    <svg viewBox="0 0 80 80" fill="none" className="w-full h-full">
      <ellipse cx="40" cy="42" rx="16" ry="20" fill="currentColor" opacity="0.8" />
      {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((angle, i) => (
        <circle key={i} cx={40 + 12 * Math.cos((angle * Math.PI) / 180)} cy={42 + 15 * Math.sin((angle * Math.PI) / 180)} r="2.5" fill="currentColor" opacity="0.5" />
      ))}
      <path d="M40 22 Q36 15, 30 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.6" />
      <path d="M40 22 Q44 14, 50 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.6" />
      <ellipse cx="30" cy="12" rx="5" ry="3" fill="currentColor" opacity="0.5" transform="rotate(-20 30 12)" />
      <ellipse cx="50" cy="13" rx="5" ry="3" fill="currentColor" opacity="0.5" transform="rotate(15 50 13)" />
    </svg>
  ),
  acacia: () => (
    <svg viewBox="0 0 80 80" fill="none" className="w-full h-full">
      <path d="M40 72 L40 35" stroke="currentColor" strokeWidth="3" strokeLinecap="round" opacity="0.5" />
      <path d="M40 35 Q20 30, 12 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
      <path d="M40 35 Q60 30, 68 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
      <ellipse cx="40" cy="22" rx="22" ry="14" fill="currentColor" opacity="0.6" />
      <ellipse cx="22" cy="20" rx="12" ry="9" fill="currentColor" opacity="0.5" />
      <ellipse cx="58" cy="20" rx="12" ry="9" fill="currentColor" opacity="0.5" />
    </svg>
  ),
  wild: () => (
    <svg viewBox="0 0 80 80" fill="none" className="w-full h-full">
      <path d="M40 72 L40 30" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" opacity="0.5" />
      <path d="M40 45 L25 32" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
      <path d="M40 40 L58 28" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
      <ellipse cx="40" cy="20" rx="10" ry="14" fill="currentColor" opacity="0.65" />
      <ellipse cx="25" cy="24" rx="9" ry="12" fill="currentColor" opacity="0.55" />
      <ellipse cx="55" cy="22" rx="8" ry="11" fill="currentColor" opacity="0.55" />
      <circle cx="40" cy="12" r="4" fill="currentColor" opacity="0.8" />
      <circle cx="25" cy="16" r="3" fill="currentColor" opacity="0.7" />
      <circle cx="55" cy="15" r="3" fill="currentColor" opacity="0.7" />
    </svg>
  ),
  sidr: () => (
    <svg viewBox="0 0 80 80" fill="none" className="w-full h-full">
      <path d="M40 72 L40 40" stroke="currentColor" strokeWidth="3" strokeLinecap="round" opacity="0.5" />
      <path d="M40 50 L28 42" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
      <path d="M40 45 L55 35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
      <ellipse cx="40" cy="28" rx="18" ry="16" fill="currentColor" opacity="0.55" />
      <circle cx="35" cy="22" r="5" fill="currentColor" opacity="0.8" />
      <circle cx="46" cy="25" r="4.5" fill="currentColor" opacity="0.75" />
      <circle cx="40" cy="32" r="4" fill="currentColor" opacity="0.7" />
      <circle cx="30" cy="30" r="3.5" fill="currentColor" opacity="0.65" />
      <circle cx="50" cy="20" r="3" fill="currentColor" opacity="0.65" />
    </svg>
  ),
};

/* ─── Varieties Grid (Animated) ─── */
const VarietiesGrid = () => {
  const [visibleItems, setVisibleItems] = useState([]);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          honeyVarieties.forEach((_, i) => {
            setTimeout(() => {
              setVisibleItems((prev) => [...prev, i]);
            }, i * 200);
          });
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="varieties" className="py-24 px-6 bg-white" ref={sectionRef}>
      <h3
        className="text-center text-2xl md:text-3xl font-bold tracking-[0.15em] uppercase text-stone-800 mb-4"
        style={{ fontFamily: "'Oswald', sans-serif" }}
      >
        Our Varieties
      </h3>
      <p className="text-center text-stone-500 mb-16 max-w-xl mx-auto" style={{ fontFamily: "'Source Serif 4', serif" }}>
        Premium honey sourced from diverse floral regions across India
      </p>
      <div className="max-w-[1100px] mx-auto grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-8">
        {honeyVarieties.map((v, i) => {
          return (
            <div
              key={i}
              className={`flex flex-col items-center group cursor-pointer transition-all duration-700 ${
                visibleItems.includes(i)
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-10"
              }`}
            >
              <div
                className="w-28 h-28 rounded-full flex items-center justify-center mb-4 transition-all duration-500 group-hover:scale-110 group-hover:shadow-xl bg-amber-50/60 overflow-hidden"
              >
                <img
                  src={v.image}
                  alt={v.name}
                  className="w-20 h-20 object-contain transition-transform duration-500 group-hover:rotate-6"
                />
              </div>
              <h4
                className="text-sm font-bold tracking-[0.1em] uppercase text-stone-800 text-center transition-colors duration-300 group-hover:text-[#FBB550]"
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                {v.name}
              </h4>
              <p
                className="mt-1.5 text-xs text-stone-400 text-center leading-relaxed max-w-[140px]"
                style={{ fontFamily: "'Source Serif 4', serif" }}
              >
                {v.description}
              </p>
            </div>
          );
        })}
      </div>
    </section>
  );
};

/* ─── Our Story ─── */
const OurStorySection = () => (
  <section id="about" className="relative overflow-hidden">
    <div className="flex flex-col lg:flex-row min-h-[600px]">
      {/* Left: Honeycomb Image */}
      <div className="lg:w-1/2 relative">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url(https://images.unsplash.com/photo-1623018697148-8350cf18e64e)" }}
        />
        <div className="absolute inset-0 bg-amber-900/30" />
        <div className="relative z-10 flex items-center justify-center h-full min-h-[350px] lg:min-h-0 px-10 py-16">
          <h2
            className="text-5xl md:text-6xl lg:text-7xl font-bold text-white uppercase leading-[0.95] text-center"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            Our
            <br />
            <span className="text-[#FBB550]">Story</span>
          </h2>
        </div>
      </div>

      {/* Right: Text Content */}
      <div className="lg:w-1/2 bg-[#FDF6EC] flex flex-col justify-center px-8 lg:px-16 xl:px-20 py-16">
        <div className="w-12 h-[2px] bg-[#FBB550] mb-8" />
        <p
          className="text-base md:text-lg text-stone-700 leading-relaxed"
          style={{ fontFamily: "'Source Serif 4', serif" }}
        >
          MB Exim is a premium exporter of bulk honey with industry experience
          spanning over 25 years. Our journey began with a passion for honey and a
          deep belief in the value of supporting small businesses.
        </p>
        <p
          className="mt-6 text-base md:text-lg text-stone-600 leading-relaxed"
          style={{ fontFamily: "'Source Serif 4', serif" }}
        >
          We take immense pride in our product and are committed to providing
          exceptional quality honey to our customers. Over the decades, we have built
          a reputation for excellence. Sourcing our honey from local beekeepers across
          diverse regions of India, we remain firm believers in supporting small
          businesses and honouring the rich heritage behind each jar. We work closely
          with our beekeepers to ensure that our honey is ethically sourced and that
          the bees are treated with care and respect.
        </p>
      </div>
    </div>
  </section>
);

/* ─── Ingredient: Pure Honey ─── */
const PureHoneySection = () => (
  <section className="bg-[#FBB550] py-20 px-6">
    <div className="max-w-4xl mx-auto text-center">
      <h2
        className="text-4xl md:text-6xl lg:text-7xl font-bold text-stone-900 uppercase italic"
        style={{ fontFamily: "'Oswald', sans-serif" }}
      >
        {pureSection.title}
      </h2>
    </div>
  </section>
);

/* ─── Certification Logos ─── */
const certLogos = [
  { name: "ISO 22000", url: "https://customer-assets.emergentagent.com/job_mbexim-studio/artifacts/0i4d6oso_images.png" },
  { name: "HACCP", url: "https://customer-assets.emergentagent.com/job_mbexim-studio/artifacts/zimp1i8j_logohccp-1.jpg" },
  { name: "Star-K", url: "https://customer-assets.emergentagent.com/job_mbexim-studio/artifacts/u6ic1abf_Star-K_logo.jpg" },
  { name: "True Source Certified", url: "https://customer-assets.emergentagent.com/job_mbexim-studio/artifacts/kn9497s3_True%20Source%20Certified%20Logo%20Registered.jpg" },
  { name: "EIC", url: "https://customer-assets.emergentagent.com/job_mbexim-studio/artifacts/e6bru7ft_EIC%20LOGO.png" },
  { name: "FDA", url: "https://customer-assets.emergentagent.com/job_mbexim-studio/artifacts/qoml2jtj_fda-logo-png-transparent.png" },
  { name: "FSSC 22000", url: "https://customer-assets.emergentagent.com/job_mbexim-studio/artifacts/04r39wid_Fssc%2022000%20logo.png" },
  { name: "Halal", url: "https://customer-assets.emergentagent.com/job_mbexim-studio/artifacts/nfvjfdre_Halal.png" },
  { name: "APEDA", url: "https://customer-assets.emergentagent.com/job_mbexim-studio/artifacts/hir8wwur_agricultural-and-processed-food-products-export-development-authority-apeda-01.jpg" },
];

/* ─── Quality Assurance ─── */
const QualitySection = () => (
  <section id="quality" className="py-20 px-6 bg-white">
    <div className="max-w-5xl mx-auto text-center">
      <h2
        className="text-3xl md:text-5xl font-bold uppercase text-stone-800 leading-tight"
        style={{ fontFamily: "'Oswald', sans-serif" }}
      >
        {qualitySection.title}
      </h2>
      <p
        className="mt-6 text-base md:text-lg text-stone-600 leading-relaxed max-w-3xl mx-auto"
        style={{ fontFamily: "'Source Serif 4', serif" }}
      >
        {qualitySection.description}
      </p>
    </div>
    {/* Full-width animated certification logos */}
    <div className="mt-14">
      <p
        className="text-center text-xs tracking-[0.2em] uppercase text-stone-400 mb-6"
        style={{ fontFamily: "'Oswald', sans-serif" }}
      >
        Our Certifications
      </p>
      <div className="overflow-hidden">
        <div className="flex items-center gap-14 animate-cert-marquee">
          {[...certLogos, ...certLogos, ...certLogos].map((cert, i) => (
            <div
              key={i}
              className="flex-shrink-0 w-[140px] h-[140px] flex items-center justify-center p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300"
            >
              <img
                src={cert.url}
                alt={cert.name}
                className="max-w-full max-h-full object-contain"
                title={cert.name}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ─── Our Services Include ─── */
const ServicesSection = () => {
  const services = [
    {
      title: "Bulk Honey Supply",
      description: "Our state-of-the-art facility near New Delhi, India, produces up to 50 metric tons of honey daily. We cater to large bulk and institutional orders for export requirements with efficiency and care.",
      image: "https://customer-assets.emergentagent.com/job_mbexim-studio/artifacts/7ejez3lp_09c995de85-ms-drum-dealers-2-dxd85.jpg.avif",
    },
    {
      title: "White-Label Packaging",
      description: "Our bottling unit ensures precision and hygiene, packaging large quantities of honey daily while maintaining the highest standards of quality and customization.",
      image: "https://customer-assets.emergentagent.com/job_mbexim-studio/artifacts/2l7xbjpe_DSC02647.JPG",
    },
  ];

  return (
    <section id="services" className="py-24 px-6 bg-white">
      <h2
        className="text-center text-3xl md:text-5xl font-bold uppercase text-stone-800 mb-20"
        style={{ fontFamily: "'Oswald', sans-serif" }}
      >
        Our Services Include
      </h2>
      {services.map((service, i) => (
        <div key={i}>
          <div
            className={`flex flex-col ${
              i % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"
            } max-w-[1200px] mx-auto mb-8`}
          >
            <div className="lg:w-1/2 overflow-hidden">
              <img
                src={service.image}
                alt={service.title}
                className="w-full h-[400px] object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div className="lg:w-1/2 flex flex-col justify-center px-8 lg:px-14 py-10">
              <h3
                className="text-2xl md:text-4xl font-bold text-[#FBB550]"
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                {service.title}
              </h3>
              <p
                className="mt-5 text-base text-stone-600 leading-relaxed"
                style={{ fontFamily: "'Source Serif 4', serif" }}
              >
                {service.description}
              </p>
            </div>
          </div>
          {i < services.length - 1 && (
            <div className="flex justify-center my-8">
              <div className="w-6 h-6 bg-[#FBB550] rotate-45" />
            </div>
          )}
        </div>
      ))}
    </section>
  );
};

/* ─── Packaging SVG Icons ─── */
const PackagingIcons = {
  bottles: () => (
    <svg viewBox="0 0 80 100" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-full h-full">
      <rect x="25" y="35" width="30" height="50" rx="3" />
      <rect x="32" y="20" width="16" height="15" rx="2" />
      <rect x="35" y="12" width="10" height="8" rx="1" />
      <path d="M15 35 L15 75 Q15 85, 25 85" />
      <rect x="8" y="20" width="14" height="15" rx="2" />
      <rect x="10" y="12" width="10" height="8" rx="1" />
      <ellipse cx="22" cy="55" rx="3" ry="4" />
    </svg>
  ),
  jugs: () => (
    <svg viewBox="0 0 80 100" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-full h-full">
      <path d="M25 25 L25 80 Q25 88, 35 88 L45 88 Q55 88, 55 80 L55 25" />
      <rect x="25" y="18" width="30" height="7" rx="2" />
      <rect x="30" y="10" width="20" height="8" rx="1" />
      <path d="M55 40 Q68 45, 65 55 Q62 65, 55 60" />
    </svg>
  ),
  pails: () => (
    <svg viewBox="0 0 80 100" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-full h-full">
      <path d="M20 35 L15 85 Q15 90, 20 90 L60 90 Q65 90, 65 85 L60 35" />
      <ellipse cx="40" cy="35" rx="20" ry="6" />
      <path d="M24 28 Q40 15, 56 28" />
    </svg>
  ),
  drums: () => (
    <svg viewBox="0 0 80 100" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-full h-full">
      <rect x="18" y="20" width="44" height="65" rx="3" />
      <ellipse cx="40" cy="20" rx="22" ry="7" />
      <ellipse cx="40" cy="85" rx="22" ry="7" />
      <line x1="18" y1="40" x2="62" y2="40" />
      <line x1="18" y1="65" x2="62" y2="65" />
    </svg>
  ),
  totes: () => (
    <svg viewBox="0 0 80 100" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-full h-full">
      <rect x="10" y="25" width="60" height="55" rx="3" />
      <rect x="20" y="15" width="40" height="10" rx="2" />
      <circle cx="25" cy="88" r="5" />
      <circle cx="55" cy="88" r="5" />
      <rect x="30" y="45" width="20" height="15" rx="1" />
      <line x1="40" y1="48" x2="40" y2="57" />
      <line x1="33" y1="52" x2="47" y2="52" />
    </svg>
  ),
  tankers: () => (
    <svg viewBox="0 0 100 80" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-full h-full">
      <rect x="5" y="25" width="30" height="25" rx="2" />
      <rect x="35" y="15" width="60" height="35" rx="15" />
      <circle cx="20" cy="62" r="7" />
      <circle cx="60" cy="62" r="7" />
      <circle cx="80" cy="62" r="7" />
      <line x1="5" y1="50" x2="35" y2="50" />
    </svg>
  ),
};

/* ─── Packaging Flexibility ─── */
const PackagingSection = () => {
  const packaging = [
    { name: "Drums", image: "https://customer-assets.emergentagent.com/job_mbexim-studio/artifacts/r7g08nlc_Drum.png", desc: "Industrial steel drums" },
    { name: "Totes", image: "https://customer-assets.emergentagent.com/job_mbexim-studio/artifacts/7j5h7cyr_totes.png", desc: "Intermediate bulk containers" },
    { name: "Tankers", image: "https://customer-assets.emergentagent.com/job_mbexim-studio/artifacts/8vn1ouzn_Tanker.png", desc: "Food-grade tanker trucks" },
    { name: "Bottles", image: "https://customer-assets.emergentagent.com/job_mbexim-studio/artifacts/6mju0pw7_Bottle.png", desc: "Squeeze & teddy bear bottles" },
    { name: "Jars", image: "https://customer-assets.emergentagent.com/job_mbexim-studio/artifacts/g0ruuizw_honey-jar.png", desc: "Glass & plastic jars" },
    { name: "Pails", image: "https://customer-assets.emergentagent.com/job_mbexim-studio/artifacts/vznzgpti_Pail%20Background%20Removed.png", desc: "Food-grade pails & buckets" },
  ];

  const [visibleItems, setVisibleItems] = useState([]);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          packaging.forEach((_, i) => {
            setTimeout(() => {
              setVisibleItems((prev) => [...prev, i]);
            }, i * 150);
          });
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-24 px-6 bg-stone-50" ref={sectionRef}>
      <div className="max-w-[1200px] mx-auto">
        <h2
          className="text-center text-3xl md:text-5xl font-bold uppercase text-stone-800 mb-4"
          style={{ fontFamily: "'Oswald', sans-serif" }}
        >
          Packaging Flexibility
        </h2>
        <p
          className="text-center text-stone-500 mb-16 max-w-2xl mx-auto"
          style={{ fontFamily: "'Source Serif 4', serif" }}
        >
          We offer a wide variety of sizes and delivery methods to meet your specific packaging needs
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-12 gap-y-16">
          {packaging.map((item, i) => (
            <div
              key={i}
              className={`group flex flex-col items-center cursor-pointer transition-all duration-700 ${
                visibleItems.includes(i)
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="w-48 h-48 flex items-center justify-center relative">
                <img
                  src={item.image}
                  alt={item.name}
                  className="max-w-full max-h-full object-contain drop-shadow-md group-hover:scale-110 group-hover:-translate-y-2 transition-all duration-500"
                />
              </div>
              <div className="mt-5 w-full text-center">
                <div className="w-10 h-[2px] bg-[#FBB550] mx-auto mb-3 group-hover:w-16 transition-all duration-500" />
                <h4
                  className="text-xl md:text-2xl font-bold tracking-[0.15em] uppercase text-stone-800 group-hover:text-[#FBB550] transition-colors duration-300"
                  style={{ fontFamily: "'Oswald', sans-serif" }}
                >
                  {item.name}
                </h4>
                <p
                  className="mt-1.5 text-sm text-stone-400"
                  style={{ fontFamily: "'Source Serif 4', serif" }}
                >
                  {item.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Clients ─── */
const ClientsSection = () => {
  const scrollRef = useRef(null);

  return (
    <section className="py-20 px-6 bg-[#FDF6EC]">
      <h3
        className="text-center text-2xl md:text-3xl font-bold tracking-[0.15em] uppercase text-stone-800 mb-14"
        style={{ fontFamily: "'Oswald', sans-serif" }}
      >
        Our Trusted Clients
      </h3>
      <div className="max-w-[1100px] mx-auto overflow-hidden">
        <div className="flex items-center gap-16 animate-marquee">
          {[...clients, ...clients, ...clients].map((c, i) => (
            <div key={i} className="flex-shrink-0 w-[140px] h-[80px] flex items-center justify-center">
              <img
                src={c.logo}
                alt={c.name}
                className="max-w-full max-h-full object-contain grayscale hover:grayscale-0 transition-all duration-300"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Contact / Newsletter ─── */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      // Save to backend DB
      axios.post(`${API}/contact`, formData).catch(() => {});

      // Send email via formsubmit.co
      const emailData = new FormData();
      emailData.append("name", formData.name);
      emailData.append("email", formData.email);
      emailData.append("message", formData.message);
      emailData.append("_subject", `New Enquiry from ${formData.name} — MB Exim Website`);
      emailData.append("_captcha", "false");
      emailData.append("_template", "table");

      await axios.post("https://formsubmit.co/ajax/info@mbexim.org", emailData);

      setSubmitted(true);
      setFormData({ name: "", email: "", message: "" });
      setTimeout(() => setSubmitted(false), 4000);
    } catch (err) {
      console.error("Contact form error:", err);
      // Even if formsubmit fails, message is saved in DB
      setSubmitted(true);
      setFormData({ name: "", email: "", message: "" });
      setTimeout(() => setSubmitted(false), 4000);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-20 px-6 bg-stone-900">
      <div className="max-w-4xl mx-auto text-center">
        <h2
          className="text-3xl md:text-5xl font-bold uppercase text-white"
          style={{ fontFamily: "'Oswald', sans-serif" }}
        >
          {contactInfo.heading}
        </h2>
        <p
          className="mt-4 text-stone-400 text-base"
          style={{ fontFamily: "'Source Serif 4', serif" }}
        >
          {contactInfo.subheading}
        </p>
        <form onSubmit={handleSubmit} className="mt-10 max-w-lg mx-auto flex flex-col gap-4">
          <input
            type="text"
            placeholder="Your Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-5 py-3.5 bg-stone-800 border border-stone-700 text-white placeholder-stone-500 focus:outline-none focus:border-[#FBB550] transition-colors"
            required
          />
          <input
            type="email"
            placeholder="Email Address"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full px-5 py-3.5 bg-stone-800 border border-stone-700 text-white placeholder-stone-500 focus:outline-none focus:border-[#FBB550] transition-colors"
            required
          />
          <textarea
            placeholder="Your Message"
            rows={4}
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            className="w-full px-5 py-3.5 bg-stone-800 border border-stone-700 text-white placeholder-stone-500 focus:outline-none focus:border-[#FBB550] transition-colors resize-none"
          />
          <button
            type="submit"
            className="w-full py-3.5 bg-[#FBB550] text-stone-900 font-bold text-sm tracking-[0.2em] uppercase hover:bg-[#E5A040] transition-colors duration-300"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            {sending ? "Sending..." : submitted ? "Message Sent!" : "Send Message"}
          </button>
        </form>
      </div>
    </section>
  );
};

/* ─── Footer ─── */
const FooterSection = () => (
  <footer className="bg-white py-16 px-6 border-t border-stone-200">
    <div className="max-w-[1200px] mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
        <div>
          <img
            src="https://customer-assets.emergentagent.com/job_mbexim-studio/artifacts/n09jef94_MB%20EXIM.png"
            alt="MB Exim Logo"
            className="h-16 w-auto object-contain"
          />
          <h3
            className="mt-3 text-base font-bold tracking-[0.1em] uppercase text-stone-800"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            MB Exim Private Limited
          </h3>
          <p
            className="mt-3 text-sm text-stone-500 leading-relaxed"
            style={{ fontFamily: "'Source Serif 4', serif" }}
          >
            India's prominent honey exporter with nearly three decades of industry experience.
          </p>
        </div>
        {footerLinks.map((group, i) => (
          <div key={i}>
            <h4
              className="text-sm font-bold tracking-[0.15em] uppercase text-stone-800 mb-4"
              style={{ fontFamily: "'Oswald', sans-serif" }}
            >
              {group.title}
            </h4>
            <ul className="flex flex-col gap-2">
              {group.links.map((link, j) => (
                <li key={j}>
                  <a
                    href={link.href}
                    className="text-sm text-stone-500 hover:text-[#FBB550] transition-colors"
                    style={{ fontFamily: "'Source Serif 4', serif" }}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
            {group.address && (
              <p
                className="mt-3 text-sm text-stone-500 leading-relaxed"
                style={{ fontFamily: "'Source Serif 4', serif" }}
              >
                {group.address}
              </p>
            )}
          </div>
        ))}
      </div>
      <div className="mt-12 pt-8 border-t border-stone-200 text-center">
        <p className="text-xs text-stone-400">
          &copy; {new Date().getFullYear()} M B Exim Pvt. Ltd. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);

/* ─── Scroll to Top ─── */
const ScrollToTop = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return visible ? (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      className="fixed bottom-8 left-8 z-50 w-12 h-12 bg-[#FBB550] text-white rounded-sm flex items-center justify-center hover:bg-[#E5A040] transition-colors duration-300 shadow-lg"
      aria-label="Scroll to top"
    >
      <ChevronLeft className="w-5 h-5 rotate-90" />
    </button>
  ) : null;
};

/* ─── WhatsApp CTA ─── */
const WhatsAppButton = () => (
  <a
    href="https://wa.me/917895054677"
    target="_blank"
    rel="noopener noreferrer"
    className="fixed bottom-8 right-8 z-50 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300"
    aria-label="Chat on WhatsApp"
  >
    <svg viewBox="0 0 24 24" fill="white" className="w-7 h-7">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  </a>
);

/* ─── Main Page ─── */
const HomePage = () => (
  <main>
    <ScrollToTop />
    <WhatsAppButton />
    <HeroSection />
    <TaglineSection />
    <VarietiesGrid />
    <OurStorySection />
    <QualitySection />
    <ServicesSection />
    <PackagingSection />
    <ClientsSection />
    <ContactSection />
    <FooterSection />
  </main>
);

export default HomePage;
